// Ячейка привязки, которая может быть унаследована от соседа.
//
// Привязка бывает проставлена у самой записи, а бывает подтянута: компания задачи
// — от её сделки, документа или контакта. Сервер считает это общим правилом
// (`link-inheritance`) и присылает унаследованные привязки отдельным полем
// `inheritedLinks`; здесь решается только, как их показать.
//
// Считать наследование на клиенте нельзя. Так делал грид сделок для одного пути в
// одну ступень, и это было терпимо; при четырёх путях и двух ступенях клиент
// повторял бы правило целиком, и первая же правка развела бы его с сервером —
// фильтр по фирме нашёл бы задачу, а колонка осталась бы пустой.
import type { ReactElement } from 'react';
import { Tooltip } from 'antd';
import { useTranslation } from '@govard/i18n-core/react';

import { EmptyLinkCell, EntityLink, type EntityKind } from './EntityLink.js';

/** Унаследованная привязка, как её присылает сервер. */
export interface InheritedLink {
  id: string;
  label: string;
  /** Ключ сущности, через которую привязка пришла: `contact`, `deal`, `document`. */
  via: string;
}

/** Запись любого списка, который умеет отдавать унаследованные привязки. */
export interface WithInheritedLinks {
  inheritedLinks?: Record<string, InheritedLink> | undefined;
}

/** Своя привязка записи, приведённая к общей форме. */
export interface OwnLink {
  id: string;
  label: string;
}

/**
 * Подпись для сортировки: сортировать надо по тому, что показано.
 *
 * Пока сортировщик смотрел только на собственную привязку, унаследованные строки
 * сортировались как пустые, но отрисовывались с названием — колонка выглядела
 * перемешанной без причины.
 */
export function linkSortLabel(own: OwnLink | null, inherited?: InheritedLink): string | null {
  return own?.label ?? inherited?.label ?? null;
}

export function LinkCell({
  kind,
  own,
  inherited,
  emptyText = '—',
  asTag = false,
}: {
  kind: EntityKind;
  own: OwnLink | null;
  inherited?: InheritedLink | undefined;
  emptyText?: string;
  asTag?: boolean;
}): ReactElement {
  const { t } = useTranslation();
  if (own !== null) {
    return <EntityLink kind={kind} id={own.id} label={own.label} asTag={asTag} />;
  }
  if (inherited === undefined) {
    return <EmptyLinkCell text={emptyText} />;
  }
  // Унаследованная — приглушённо и с подсказкой, которая называет ЗВЕНО. «Связь
  // через контакт» на привязке, пришедшей через сделку, — такая же неправда, как
  // показать её обычной ссылкой.
  return (
    <Tooltip
      title={t('crm.shared.inherited_link_tooltip', {
        via: t(`crm.shared.inherited_via.${inherited.via}`),
      })}
    >
      <span className="opacity-70 [&_a]:text-stone-500 [&_.ant-tag]:opacity-80">
        <EntityLink kind={kind} id={inherited.id} label={inherited.label} asTag={asTag} />
      </span>
    </Tooltip>
  );
}
